define((require) => {
    "use strict";

    const _               = require("underscore");
    const EventDispatcher = require("shared/util/EventDispatcher");
    const RestApi         = require("shared/services/RestApi");
    const Syncer          = require("shared/services/sync/Syncer");
    const TargetQueue     = require("shared/services/sync/TargetQueue");
    const SequentialQueue = require("shared/services/sync/SequentialQueue");
    const Preconditions   = require("shared/util/Preconditions");
    const ParallelQueue   = require("shared/services/sync/ParallelQueue");
    const Promise         = require("shared/util/Promise");
    const UrlUtil         = require("shared/util/UrlUtil");
    const {Logger}          = require("shared/util/Logger");

    const log = new Logger("SyncManager");
    /**
     * @class {SyncManager}
     * A <i>sync manager</i> that will handle all target sync requests against some deferred service. <p>
     *
     * The following traits will hold for any target instance synced using this sync manager: <ul>
     *
     * <li> "Horizontal queueing": different target instances (with different ids) can be ordered so the i'th target will
     *      complete its sync before the i+1'th target will start its sync. This is done by supplying an ordered array of
     *      targets to the <tt>sync</tt> function, like <tt>sync(..)</tt>, like <tt>sync("save", [modelA, modelB, modelC])</tt>.
     *      Here, "modelA.save()" will complete before "modelB.save()" starts, and "modelB.save()" will complete
     *      before "modelC.save()" starts. <p>
     *
     *      Relevant queues are sequential and parallel queues. <p>
     *
     * <li> "Vertical queueing": the same target instance (with the same id) cannot have more than one executing sync
     *      request, all others will be queued. This means that when trying to sync a target where sync requests are
     *      already pending, the new request will be queued. So, <tt>sync("save", modelA)" will block any other sync
     *      request for "modelA" while pending, like <tt>sync("fetch", modelA)</tt>. It also means that an array of
     *      ordered targets like in the bullet above ([modelA, modelB, modelC]) can be blocked at any place because of
     *      pending requests for the target (model) in question: here, the array will be blocked already on "modelA" it
     *      is has pending requests (and the same goes for all other objects). <p>
     *
     *      Relevant queue is a target queue. <p>
     *
     * <li> Future requests will not block earlier requests.
     *
     * </ul>
     *
     * Different ways of syncing: <ol>
     *
     * <li> "Vertical queueing" - single target:
     *       <pre>
     *          return manager.sync("save", modelA, ..).promise().done(..).fail(..);
     *       </pre>
     *
     * <li> "Vertical queueing" - single target (same as above, though more verbose):
     *       <pre>
     *          var queue = manager.queue(modelA);
     *          var promise = queue.enqueue("save", ..).done(..).fail(..); // note: promise handlers on "enqueue" result, when the enqueued request is complete
     *          queue.sync();
     *          return promise;
     *       </pre>
     *
     * <li> "Horizontal sequential queueing" - multiple targets, single sync method:
     *       <pre>
     *         return manager.sync("save", [modelA, modelB, modelC], ..).promise().done(..).fail(..);
     *       </pre>
     *
     * <li> "Horizontal sequential queueing" - multiple targets, multiple sync methods:
     *       <pre>
     *         return manager.queue() // or .queue(false)
     *             .enqueue("save", modelA, ..)
     *             .enqueue("publish", modelB, ..)
     *             .enqueue("save", [modelC, modelD], ..)
     *             .sync()
     *             .promise()
     *             .done(..).fail(..); // note: promise handlers on "sync" result, when all sync requests are complete
     *       </pre>
     *
     * <li> "Horizontal parallel queueing" - multiple targets, multiple sync methods:
     *       <pre>
     *         return manager.queue(true) // true = parallel
     *             .enqueue("save", modelA, ..)
     *             .enqueue("publish", modelB, ..)
     *             .enqueue("save", [modelC, modelD], ..)
     *             .sync()
     *             .promise()
     *             .done(..).fail(..); // note: promise handlers on "sync" result, when all sync requests are complete
     *       </pre>
     *
     * </ol>
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     *
     */
    const SyncManager = EventDispatcher.extend("SyncManager", {

        /**
         * @private {SequentialQueue[]|ParallelQueue[]}
         * Queue of sequential and/or parallel queues. <p>
         *
         * Individual queues may or may not block each other, and may be executing currently. For example, two
         * different queue may reference different targets entirely, or there may be an overlap. On overlap,
         * the first added queue will block the latter queue (at some point). <p>
         *
         * Once a queue is empty, it will be discarded by sync manager altogether.
         */
        _bulkQueues: null,
        /**
         * @private @type {Map<*,TargetQueue>}
         * Map of target instances/identifiers -> target queue per target entity (instance). The same queue
         * may be indexed by numerous identifiers, but always by the target itself at least. <p>
         *
         * Once a queue is empty, it will be discarded by this sync manager altogether.
         *
         * @see #_targetQueuesSize
         */
        _targetQueues: null,
        /**
         * @private @type {Number}
         * The number of unique target queues currently in <tt>_targetQueues</tt>.
         *
         * @see #_targetQueues
         */
        _targetQueuesSize: null,
        /**
         * @private @type {Map<*,Syncer>}
         * Map of target syncers in play, will only differ from <tt>_targetQueues</tt> in case we have pending
         * sync requests on for example debounced syncing, such as debounced sync in the editor.
         */
        _syncers: null,
        /**
         * @private @type {Number}
         * The current number of actively dequeuing queues, i.e. where syncs are in progress (i.e. a blocked queue
         * will not be included in this count). <p>
         *
         * Note the number does not say anything about the number of active targets, since it reflects all queues. <p>
         *
         * A change in this number will cause this manager to trigger events.
         */
        _dequeuing: null,
        /**
         * @private @type {*}
         * Options for this sync manager. <p>
         *
         * Can contain:
         * <pre>
         *    {
         *       timeout: Number // sync timeout in ms, defaults to 20s, for individual sync requests
         *    }
         * </pre>
         */
        _options: null,

        /**
         * @constructor
         * Constructor, which creates this sync manager.
         *
         * @param {*} [options] The options to use for this manager; will default.
         */
        constructor(options) {
            this._options = options || {};
            this._bulkQueues = [];
            this._targetQueues = new Map();
            this._targetQueuesSize = 0;
            this._syncers = new Map();
            this._dequeuing = 0;
        },

        /**
         * Returns the globally unique target identifier(s) for the supplied target.
         *
         * @param  {*}   target The target to find its current globally unique identifiers for; cannot be falsey.
         *                      Any target must be a mutable object type since it will (eventually) be assigned a
         *                      <tt>syncState</tt> property, if not already.
         * @return {[]}  The globally unique target identifier(s); never empty, will always contain <tt>target</tt>.
         * @throw  Error If <tt>target</tt> is a non-boolean, non-mutable object type (e.g. a string).
         */
        targetIdentifiers(target) {
            const unique = Syncer.State.state(target).unique;
            // Function to return unique identifiers...
            if (_.isFunction(unique)) {
                return unique(target);
            }
            // Props...
            return this._targetIdentifiers(target, unique);
        },

        /**
         * @private
         * Returns a new array of all currently defined target identifiers for the supplied target, where only those
         * properties storing a defined identifiers in <tt>props</tt> will cause an identifier to be returned. <p>
         *
         * The returned array will always contain <tt>target</tt> as the first index, i.e. can uniquely identify
         * its own instance.
         *
         * @param {*}        target The target to find the identifiers for; cannot be falsey.
         * @param {String[]} props  The properties in the synced target that can store a globally unique identifier for
         *                          it (besides the target instance itself); never falsey. Properties will be tried
         *                          looked up based on Backbone getter, secondly as a normal property.
         * @return {[]} An array of available globally unique identifiers; never falsey, and never empty (will always
         *              contain the supplied target instance at least).
         */
        _targetIdentifiers(target, props) {
            // Target *instance* is also a target identifier by itself...
            const identifiers = [target];

            const prop = (name) => {
                if (_.isFunction(target.get)) {
                    return target.has(name) && target.get(name);
                }

                return _.result(target, name);
            };

            let identifier;
            let name;
            let i;

            for (i = 0; i < props.length; i++) {
                name = props[i];
                identifier = prop(name);
                if (identifier) {
                    identifier = `${name}:${identifier}`;
                    if (target.__typeName) {
                        identifier = `${target.__typeName}:${identifier}`;
                    }
                    identifiers.push(identifier);
                }
            }
            return identifiers;
        },

        /**
         * Returns a (new) <i>target queue</i>, a new <i>sequential queue</i>, <<b>or</b> a new <i>parallel queue</i>. <p>
         *
         * In case a pre-existing target queue is returned, it may already be syncing/dequeuing! Freshly created
         * queues, regardless of type, are not started. <p>
         *
         * See class documentation for example use-cases.
         *
         * @param {*|Boolean} [target]        The target used for the target queue, or false for a sequential queue or true
         *                                    for a parallel queue to be created and returned. All non boolean types must
         *                                    be a mutable object type since <tt>target</tt> will then be assigned a
         *                                    <tt>syncState</tt> property, if not already.
         * @param {Function}  [resultHandler] The result handler to transform results for the created queue, if any.
         *                                    Will default so each target synced will be resolved as-is, in queued
         *                                    order for bulk queues, and a single target for target queues.
         * @param {Boolean}  [doNotCreate]    True to return null if no such target queue exists or is stale, false to
         *                                    create and register a new target queue (default). Ignored for any
         *                                    type of queue except target queues.
         *
         * @return {Queue} The corresponding (new) queue; can ever only be null if <tt>doNotCreate</tt> is true.
         *                 If a queue is returned, it will be discarded once it becomes empty again.
         * @throw  Error   If <tt>target</tt> is a non-boolean, non-mutable object type (e.g. a string).
         */
        queue(target, resultHandler, doNotCreate) {
            if (_.isUndefined(target)) {
                target = false; // sequential
            }

            // Sequential or parallel bulk queue?
            if (_.isBoolean(target)) {
                // No explicit target, so we create a sequential or parallel queue as specified, where targets can be
                // added to before synced...
                const q = target ? new ParallelQueue(this, resultHandler) : new SequentialQueue(this, resultHandler);
                this._bulkQueues.push(q);
                // Remember to clean up when empty...
                q.promise().always(() => {
                    const i = this._bulkQueues.indexOf(q);
                    if (i !== -1) {
                        this._bulkQueues.splice(i, 1);
                        log.verbose(`Discarded queue: ${q} -> ${this}`, q, this);
                    }
                });
                return q;
            }

            // If we have an individual target, we (re-)use a target queue based on target identifier(s) (which may
            // be the target instance itself)...
            const identifiers = this.targetIdentifiers(target);

            let tq = null;
            let i;

            for (i = 0; i < identifiers.length; i++) {
                tq = this._targetQueues.get(identifiers[i]);
                // Find *active* queue, if possible...
                if (tq && !tq._stop) {
                    break;
                }
            }
            if (!tq || tq._stop) {
                // do not reuse stale target queues...

                // If true, only return existing queues, used for testing queue existence...
                if (doNotCreate) {
                    return null;
                }

                tq = new TargetQueue(this, this._syncer(target, identifiers), resultHandler);
                // Remember to clean up when empty...
                tq.promise().always(() => {
                    this._unregisterQueue(tq);
                    log.verbose(`Discarded queue: ${tq} -> ${this}`, tq, this);
                });

                this._registerQueue(tq, identifiers);
            }
            return tq;
        },

        /**
         * @protected
         * Registers the supplied target queue, and optionally its associated syncer, as in use by this
         * sync manager. <p>
         *
         * Since a target may changes its global unique identifiers over time, i.e. on creation or simply because it
         * has several unique properties, this is needed to associate the same target queue instances will numerous
         * global target identifiers for it. When the queue is depleted, all such identifiers will be cleared.
         *
         * @param {TargetQueue}  queue         The target queue to register; cannot be falsey.
         * @param {[]}           [identifiers] The target identifier(s) to register the target queue by, defaulting to
         *                                     the identifiers calculated "now".
         */
        _registerQueue(queue, identifiers) {
            if (arguments.length === 1) {
                identifiers = this.targetIdentifiers(queue._syncer.target);
            }
            // We know we have a new queue if the target itself is not in the map as "identifiers" will always contain
            // at least the synced target itself...
            const newQueue = !this._targetQueues.has(queue._syncer.target);

            let identifier;
            let i = identifiers.length;
            let j;

            if (newQueue) {
                this._targetQueuesSize++;
            }

            loop:
            while (i--) {
                identifier = identifiers[i]; // possible new identifier...
                for (j = 0; j < queue._identifiers.length; j++) {
                    // Does it already exist? If so, we ignore it...
                    if (queue._identifiers[j] === identifier) {
                        continue loop;
                    }
                }
                // Identifier is new, so register it for the queue/syncer...
                queue._identifiers.push(identifier);
                // Register queue and syncer itself, so we know the correlate...
                this._targetQueues.set(identifier, queue);
                this._syncers.set(identifier, queue._syncer);
            }
        },

        /**
         * @private
         * Unregisters the supplied target queue after it has stopped (depleted). The queue will thus be completed
         * discarded, never to be used again.
         *
         * @param {TargetQueue} queue The target queue to unregister; never falsey.
         * @see   #_registerQueue
         */
        _unregisterQueue(queue) {
            let identifier;
            let i;

            for (i = 0; i < queue._identifiers.length; i++) {
                identifier = queue._identifiers[i];
                this._targetQueues.delete(identifier);
                this._syncers.delete(identifier);
            }
            this._targetQueuesSize--;
        },

        /**
         * @protected
         * Queue callback to keep track of overall sync state for this sync manager. <p>
         *
         * On actual state change, the "syncing" event will be triggered, with two arguments: a boolean flag
         * that will be true if this manager went from not syncing to syncing, or false if it went from
         * syncing to no longer syncing; and this manager as the second argument.
         *
         * @param {Boolean} True if the caller queue is currently active dequeuing stuff, false if it is not.
         *
         * @see   #syncing
         */
        _state(dequeuing) {
            let trigger = false;
            let syncing = false;

            // Dequeuing started, a count of one now?
            if (dequeuing) {
                if (++this._dequeuing === 1) {
                    trigger = true;
                    syncing = true;
                }

            // Dequeuing all done, back to zero?
            } else if (--this._dequeuing === 0) {
                trigger = true; // syncing already false
            }

            if (trigger) {
                log.verbose(`Changed sync state: ${this} -> ${syncing}`);
                this.trigger("syncing", syncing, this);
            }
        },

        /**
         * Returns true if this sync manager is current syncing (in the background), false otherwise. <p>
         *
         * State changes will also be notified via the event "syncing", with two arguments: a boolean flag
         * that will be true if this manager went from not syncing to syncing, or false if it went from
         * syncing to no longer syncing; and this manager as the second argument.
         *
         * @return {Boolean} True if this sync manager is current syncing, false otherwise.
         */
        syncing() {
            return !!this._dequeuing;
        },

        /**
         * @private
         * Returns a (new) syncer to (eventually) invoke sync requests for the supplied target. <p>
         *
         * An existing syncer may be returned in case the target in question has pending sync requests, such as
         * for example a debounced save in the editor. <p>
         *
         * Note that <tt>target</tt> cannot be a read-only value, as it will be augmented with a
         * <tt>syncState</tt> property, e.g. cannot be a string.
         *
         * @param  {*} target        The target to (eventually) invoke sync requests for; cannot be falsey. Must be a
         *                           type that is an object, e.g. cannot assign properties to primitive types.
         * @param  {*} [identifiers] The specified identifier(s) to use for <tt>target</tt>, defaulting to the current
         *                           target identifiers of <tt>target</tt> if not supplied.
         * @return {Syncer} A (new and) configured syncer for <tt>target</tt>; never null.
         * @throw  Error If <tt>target</tt> is not a mutable object.
         */
        _syncer(target, identifiers) {
            Preconditions.checkState(_.isObject(target), new Error(`Target must be an object, not: '${target}'`));
            if (arguments.length === 1) {
                identifiers = this.targetIdentifiers(target);
            }
            let syncer;
            let i;

            for (i = 0; i < identifiers.length; i++) {
                syncer = this._syncers.get(identifiers[i]);
                if (syncer) {
                    break;
                }
            }
            if (!syncer) {
                syncer = new Syncer(this, target, this._options.timeout);
                for (i = 0; i < identifiers.length; i++) {
                    this._syncers.set(identifiers[i], syncer);
                }
            }
            return syncer;
        },

        /**
         * Blocks <tt>target</tt> until each target to wait for (those in <tt>waitFor</tt>) have a blocking await
         * request at the head of their individual target queues (enqueued here). The effect of this is to ensure
         * that one target can be blocked while other requests for other targets are syncing, while blocking said
         * targets while the target it self is syncing. An example could be to wait for all assets in a group to
         * have synced before pushing the group and assets. <p>
         *
         * The return value is a "wait function" that must be invoked to actually start the waiting. This function
         * will enqueue relevant blocking requests, but the individual queues will not be actually unblocked until
         * the wait function has been called, even if the blocking await requests for each target in <tt>waitFor</tt>
         * is already at the head of their respective queues. This is to ensure the calling context can enqueue
         * additional requests for <tt>target</tt> to be synced before we allow the wait for target queues to
         * proceed. <p>
         *
         * In addition, a set of sync options and var-args can be specified, which will enqueue such a sync request
         * for each wait for target individually, but not for <tt>target</tt>, immediately after the block request
         * to wait for. The effect of this is that such request will be executed immediately after the await request
         * is unblocked per target.
         *
         * @param  {*}        target   The target to block until <tt>waitFor</tt> are all stable <b>after</tt> the
         *                             returned wait function has been called; cannot be falsey.
         * @param  {*|[]}     waitFor  The target(s) to wait for before <tt>target</tt> is unblocked; cannot be falsey.
         *                             While the target queue for <tt>target</tt> is then syncing actively, all these
         *                             targets will be blocked until the target queue for <tt>target</tt> is empty.
         * @param {String|*} [options] Optional sync method name, e.g. "_save" or "save", or sync options for a sync
         *                             request to enqueue after the block request per wait for target (not for
         *                             <tt>target</tt>!); can be falsey.
         * @param {...*}     [argumentsJsDoc] Arguments, as var-args, for the actual sync method when invoked if <tt>options</tt>
         *                             is specified, if any. Will default to an empty array of arguments.
         *
         * @return {Function} An wait function to call once the target queue for <tt>target</tt> is ready to start
         *                    waiting; never falsey. The return value of the wait function is a promise that will
         *                    resolve once the entire await logic is done. Must be call to start the actual waiting;
         *                    until called, the queues in question (i.e. those for <tt>target</tt> and each target
         *                    in <tt>waitFor</tt> can still be changed without them ever getting past the enqueued
         *                    blocked requests).
         *
         * @see    #block
         * @see    Queue#block
         * @see    Queue#sync
         */
        // eslint-disable-next-line no-unused-vars
        await(target, waitFor, options, argumentsJsDoc, ...args) {
            if (!_.isArray(waitFor)) {
                waitFor = [waitFor];
            }

            const n = waitFor.length;
            const promises = [];
            const queues = [];

            let blocked = "await";
            let notify = false;
            let promise;
            let tq;
            let i;

            // Target unblocker, implicitly creating a target queue for the target if not already. The enqueued
            // block request will thus go to the end of the queue since blocked...
            const targetUnblock = this.block(target);

            // Wait for unblocker...
            const waitForUnblock = function() {
                if (blocked) {
                    blocked = false;
                    // Try and sync each queue, but they may still be blocked...
                    for (let j = 0; j < n; j++) {
                        queues[j].queue.sync();
                    }
                }
            };

            const blockedHandler = function() {
                if (notify && n === queues.length) { // latter check because we call "sync" on each target queue after queuing a "block" request...
                    let entry;
                    let j;
                    // Check each queue and make sure to (re-)start syncing if it was blocked because of our blocker...
                    for (j = 0; j < n; j++) {
                        entry = queues[j];
                        if (entry.queue.head() !== entry.entry) {
                            return blocked;
                        }
                    }
                    notify = false;
                    targetUnblock();

                    // Fall-through - still blocked until unblocker called!
                }

                return blocked;
            };

            // Find the target queue for each target to wait for and block it. We get a promise for each the tail of
            // each queue, which is either the block requests or the request enqueued after it based on "options"
            // and "varArgs"...
            for (i = 0; i < n; i++) {
                tq = this.queue(waitFor[i]); // may already be syncing...
                queues.push({queue: tq, entry: tq.block(blockedHandler).entry});

                // Enqueue a sync request *per* queue to be dequeued once the unblocker is called?
                if (options) {
                    const enqueueArgs = [argumentsJsDoc].concat(args);
                    tq.enqueue(options, enqueueArgs); // varArgs -> []
                }

                // Get promise from the tail at this point in time, to be combined into a single promise to eventually
                // return by the return wait function to represent the completion of this entire await operation...
                promises.push(tq.tail().promise());
            }

            // Save a reference to the actual target queue for the wait function below...
            tq = this.queue(target);

            // Return wait function, to start the logic to (eventually) unblock "waitFor" targets once the
            // target queue for "target" is ready, i.e. added additional requests to it. The wait function will
            // return the promise that will resolve once the entire await operation has completed, i.e. after
            // all "waitFor" targets have been unblocked after "target" has been synced...
            return () => {
                if (!promise) {
                    notify = true;

                    // Make sure we call "waitForUnblock" once the target is unblocked and have synced with regards to
                    // this await invocation. The block of "target" added above is guaranteed to be in the target queue
                    // at this point since blocked by design, but other sync requests can (will) have been added
                    // after "await" was called for "target" and until this wait function is called. We thus know
                    // we have a valid tail of the target queue for "target", which will be the block request if nothing
                    // else have been enqueued. We cannot simply wait for the entire target queue to finish, since
                    // more stuff can be added to it while we wait...
                    tq.tail().promise().always(waitForUnblock); // always: to ensure cancel and failure also handled

                    for (let j = 0; j < n; j++) {
                        queues[j].queue.sync();
                    }

                    // Get a combined promise for all "waitFor" target queues...
                    promise = Promise.all(promises);
                }
                return promise;
            };
        },

        /**
         * Enqueues an immutable sync method that will simply return each target when synced (resolved), but effectively
         * blocking each supplied target until the returned unblocker function has been called to unblock each block
         * request (per target) just enqueued. This is useful to block targets across different queues. <p>
         *
         * Even though the returned unblocker has been called, individual targets may still be blocked from
         * elsewhere! <p>
         *
         * In addition, a set of sync options and var-args can be specified, which will enqueue such a sync request
         * for each target individually (using the same target queue) immediately after the block request, i.e. will
         * simply call <tt>sync(options, target[i], var-args)</tt>. The effect of this is that such request will be
         * executed immediately after the block request is unblocked, per target.
         *
         * @param  {*|[]}     targets  The target(s) to enqueue a blocking sync request for; cannot be falsey.
         * @param {String|*} [options] Optional sync method name, e.g. "_save" or "save", or sync options for a sync
         *                             request to enqueue after the block request per target; can be falsey.
         * @param {...*}     [argumentsJsDoc] Arguments, as var-args, for the actual sync method when invoked if <tt>options</tt>
         *                             is specified, if any. Will default to an empty array of arguments.
         *
         * @return {Function} An unblocker function to call once the blocking just enqueued should cease to block;
         *                    never falsey. If <tt>options</tt> (and optionally <tt>varArgs</tt>) is supplied, the
         *                    corresponding sync request will be dequeued once the unblocker is called.
         *
         * @see    #await
         * @see    Queue#block
         * @see    Queue#sync
         */
        // eslint-disable-next-line no-unused-vars
        block(targets, options, argumentsJsDoc, ...args) {
            const blockedHandler = () => blocked;
            const queues = [];

            let blocked = "blocked";
            let tq;
            let i;

            if (!_.isArray(targets)) {
                targets = [targets];
            }
            // Find the target queue for each target and block it...
            for (i = 0; i < targets.length; i++) {
                tq = this.queue(targets[i]);
                queues.push({queue: tq, entry: tq.block(blockedHandler).entry});

                // Enqueue a sync request *per* queue to be dequeued once the unblocker is called?
                if (options) {
                    const enqueueArgs = [argumentsJsDoc].concat(args);
                    tq.enqueue(options, enqueueArgs); // varArgs -> []
                }
            }

            // Unblocker function...
            return () => {
                if (blocked) {
                    blocked = false;
                    // Try and sync each queue, but they may still be blocked...
                    for (let j = 0; j < queues.length; j++) {
                        queues[j].queue.sync();
                    }
                }
            };
        },

        /**
         * Cancels any pending or queue sync methods with the specified name for the specified target, returning
         * true if actual cancellation took place, false otherwise.
         *
         * @param  {*}      target The target in question; cannot be falsey.
         * @param  {String} method The sync method for <tt>target</tt> cancel; cannot be falsey.
         *
         * @return {Boolean} True if (at least one) such a request was cancelled, false otherwise.
         * @throw  Error If <tt>target</tt> is not a mutable object.
         *
         * @see    #sync
         * @see    Queue#cancel
         */
        cancel(target, method) {
            // Since we know all sync requests will eventually be queued via a target queue for any target in
            // question, we get said queue, if it exists, and cancel where applicable: any pending, including queued,
            // sync request with the specified type will be cancelled. This is needed because even though we drop
            // successive identical sync methods on queuing, sync methods can still alternate like "save", "push",
            // "save", "push", etc., i.e. same sync method can be present in the queue. However, this may leave
            // the same type of sync methods in the queue, e.g. "push" followed by "push", but for simplicity we leave
            // them be, since we have already given out promises for their completion. A bit more work will then
            // be performed, but it is a rare case anyway and no need to make the queue implementation even more
            // complex...
            const tq = this._targetQueues.get(target);

            let cancelled = false;

            if (tq) {
                cancelled = tq.cancelMethod(method);
            }

            return cancelled;
        },

        /**
         * Short-cut for calling <tt>Syncer.cancelable(promise)</tt> on the syncer used <tt>target</tt>. <p>
         *
         * This function should be called from within any sync method logic using an external promise that should
         * be cancelable, e.g. to mask a promise for a http request doing the actual work.
         *
         * @param  {*}               target  The target in question; cannot be falsey.
         * @param  {Promise|Boolean} promise The actual promise describing the actual sync method, e.g. the promise for a
         *                                   http request to save <tt>target</tt>; or a boolean false value when
         *                                   Backbone will not do a server round trip, like when destroying an
         *                                   unsaved model. In the latter case, this function will return a resolved
         *                                   promise with <tt>target</tt> as the resolved value.
         *
         * @return {Promise} A new promise to use in place of <tt>promise</tt>; never falsey. If <tt>promise</tt> is
         *                   false, a new promise already resolved to <tt>target</tt> is returned.
         *
         * @see    Syncer#cancelable
         * @see    #pending
         * @see    #sync
         */
        cancelable(target, promise) {
            return this._syncer(target).cancelable(promise); // TODO: TargetQueue instead? Like on cancel()?
        },

        /**
         * Convenience function to listen for a ("sync:state", state, method, target) event for the specified target(s),
         * one-time only or continuously. <p>
         *
         * Options can have the following format:
         * <pre>
         *   {
         *      target:   *|Collection,                // mandatory, the target(s) to listen for "sync:state" event(s),
         *                                             // optionally in a Backbone collection.
         *      callback: Function,                    // mandatory, a standard listener for "sync:state" events on
         *                                             // <tt>options.target</tt>, having the format
         *                                             // <tt>fn(state:Syncer.State, method:String, error:*, target:*)</tt>.
         *                                             // Will be invoked with the relevant target scope.
         *      state:    Syncer.State|Syncer.State[], // optional, the specific Syncer.State(s) to listen for,
         *                                             // defaulting to <tt>Syncer.State.Synced</tt> if undefined. Use
         *                                             // null to be notified on any state.
         *      method:   String|String[],             // optional, the specific sync method(s) to listen for, defaulting
         *                                             // to any method (combined with <tt>options.state</tt>).
         *      once:     Boolean,                     // optional, true to only listen for the first such sync state
         *                                             // event, false to listen continuously (default).
         *      context:  *                            // optional, the context listening for the "sync:state" event(s),
         *                                             // defaulting to <tt>options.target</tt>.
         *   }
         * </pre>
         *
         * <tt>options.context</tt> must support Backbone's <tt>listenTo</tt> and <tt>stopListening</tt> to functions,
         * and <tt>options.targets</tt> must support Backbone's <tt>trigger/tt> function for this to work. <p>
         *
         * @param {*} options The options as described above; cannot be falsey.
         * @return {Function} A no-arg function to remove the callback manually, if not already removed; if so, the
         *                    returned function will be a no-op when invoked.
         * @throws {Error} If <tt>context</tt> does not support Backbone's <tt>listenTo</tt> and <tt>stopListening</tt>
         *                 functions.
         * @see    #promise
         */
        onSync(options) {
            // null to allow a default or all (Synced on undefined, all on null)...
            const context = options.context || options.target;

            let state   = options.state === null ? null : (options.state || Syncer.State.Synced);
            let method  = options.method;

            if (state && !_.isArray(state)) {
                state = [state];
            }
            if (method && !_.isArray(method)) {
                method = [method];
            }

            // The sync:state handler will always be supplied the actual target as the last argument, which we have
            // to use here, as "options.target" can be a Backbone Collection that will re-trigger events from
            // contained models (targets)...
            const handler = (s, m, error, target) => {
                if ((!state || state.indexOf(s) !== -1) && (!method || method.indexOf(m) !== -1)) {
                    if (options.once) {
                        remove();
                    }
                    options.callback.call(target, s, m, error, target);
                }
            };

            const remove = () => {
                context.stopListening(options.target, "sync:state", handler);
            };

            context.listenTo(options.target, "sync:state", handler);
            return remove;
        },

        /**
         * Returns a promise that will resolve when no sync requests are syncing and/or queued from the supplied
         * targets, or be rejected on error or because sync requests keep getting enqueued or on a specified allow
         * timeout to wait. <p>
         *
         * If at call time no queues are active for the target(s), the returned promise will already have been
         * resolved (and <tt>done</tt> handlers will fire immediately).
         *
         * @param {*|[]}    targets   The target(s) to wait for, either a single target or an array of targets; cannot be falsey.
         * @param {Number} [timeout]  The timeout in milliseconds to wait until the returned promise should be rejected;
         *                            default no timeout.
         * @param {*}      [deferred] The backing deferred (private, used for recursion).
         *
         * @return {Promise} A promise that will resolve when no active and/or queued sync requests for the specified
         *                   target(s); never falsey.
         * @see    #onSync
         */
        promise(targets, timeout, deferred) { // "deferred" private, used on recursion!
            if (!deferred) {
                deferred = Promise.deferred();
            }

            let resolved = true;

            // Simply optimisation attempt: no target queues mean no syncing, so we can return resolved promise as-is...
            if (this._targetQueuesSize) {
                if (!_.isArray(targets)) {
                    targets = [targets];
                }

                const promises = [];

                let tq;
                let i;

                for (i = 0; i < targets.length; i++) {
                    // We know each target will always reference its own queue, perhaps with other identifiers, but
                    // always by its own instance, so it suffices to check for this here...
                    tq = this._targetQueues.get(targets[i]);
                    if (tq && !tq._stop) {
                        promises.push(tq.promise());
                    }
                }

                // Since we may have any number of pending queues, we cannot simply use the resolved
                // value from it. Zero pending requests means that we are ready for "targets"...
                if (promises.length) {
                    let promise = promises.length === 1 ? promises[0] : Promise.all(promises);

                    if (Promise.isPending(promise)) {
                        promise = promise.done(() => {
                            // Recurse, as we could have had concurrently pending sync requests; we no longer applicable,
                            // we will have more promises, or queues at all, and we will fall through to resolving below.
                            // We must cancel pending timeout, and subtract already used time...
                            if (timeout && _.isFunction(promise.clearTimeout)) {
                                timeout -= promise.clearTimeout();
                            }
                            // No need to return a new promise here, since the promises are all internal - we ever
                            // only return promises based on "deferred"...
                            this.promise(targets, timeout, deferred);
                        });

                        if (timeout) {
                            Promise.time(deferred, promise, timeout);
                        }
                        resolved = false;
                    }
                }
            }

            if (resolved) {
                deferred.resolve(targets);
            }

            // Return the *public* promise then, always from the (recursively) defined deferred...
            return deferred.promise();
        },

        /**
         * Performs the specified sync method on the supplied target(s), returning a promise that will resolve or
         * reject when the sync request(s) has (have) completed successfully or (just one) failed, respectively. <p>
         *
         * <tt>options</tt> can have the following structure (or be a string):
         * <pre>
         * {
         *   method:        String,            // mandatory: logical name used for triggering sync:state events, e.g. "save" or "_save".
         *   fn:            Function|*,        // optional: function to perform the work; ajax options if "http" is true; or result to
         *                                     // return as-is otherwise. If not specified, the function is looked up the target(s) based
         *                                     // on "method". Will be invoked with target scope.
         *   silent:        Boolean,           // true to suppress "sync:state" events for the request, false otherwise (default).
         *   parallel:      Boolean,           // optional: true for parallel execution if multiple targets, false (default) for sequential execution.
         *   promise:       Boolean,           // optional: true to return a <tt>Promise</tt> for the request just enqueued,
         *                                     // false to return the <tt>Queue</tt> in question (default).
         *   delay:         Number,            // optional: debounce delay in ms before the sync request can be executed: will
         *                                     // be debounced if an equivalent request is enqueued in the queue within this time span.
         *   http:          Boolean,           // optional: true for http sync requests (default), false otherwise. Used to
         *                                     // determine http error codes.
         *   immutable:     Boolean,           // optional: true if the sync request will is read-only, false if it is assumed
         *                                     // to updates stuff/the target (default).
         *   validate:      Function|Boolean,  // optional: a function to return null on success, <tt>Syncer.State.Cancel</tt> to
         *                                     // cancel the request at request invocation time, or any other value to be raised
         *                                     // as an error at request invocation time. Default null. Invoked with target scope after
         *                                     // "privilege" or "role" has been tested.
         *   resultHandler: Function,          // optional: function to transform each sync method result before resolved; will
         *                                     // default to the synced target for target queues, and to all synced
         *                                     // targets for bulk queues, in queuing order. Invoked with target scope.
         *   errorHandler:  Function,          // optional: error handler passed to the ErrorHandler type to process errors. Invoked
         *                                     // with target scope.
         *   cancelable:    Function,          // optional: a function that will determine if the request is considered cancelable
         *                                     // or not. Any request can be cancelled, but the actual promise may resolve later,
         *                                     // e.g. if cancelled because of timeout. This function determines if such a
         *                                     // "late resolve" should be allowed to update the target or not. For versioned models,
         *                                     // for example, they should be allowed on updates or a 409 is likely to
         *                                     // follow on next update. For this to make sense, the actual promise
         *                                     // should be wrapped via SyncManager.cancelable(target, promise).
         *   privilege:     Privilege,         // optional: privilege to be allowed to do the sync request, if any, OR
         *   role:          Role               // optional: role to be allowed to do the sync request, if any (privilege takes precedence)
         * }
         * </pre>
         *
         * If <tt>options</tt> is a string, say "_save", it is treated like this structure for each synced target:
         * <pre>
         *   {
         *       method:     "save",          // "save" -> "save", "_save" -> "save"
         *       fn:         target["_save"], // can be private
         *       silent:     false,           // not silent, i.e. trigger "sync:state" for targets that supports "trigger"
         *       parallel:   false,           // not parallel
         *       http:       true,            // assumed http
         *       immutable:  false,           // assumed mutable
         *       promise:    false            // returns the queue, not a promise
         *   }
         * </pre>
         *
         * @param {String|*} options  The sync method name, e.g. "_save" or "save", or options like described
         *                            above; never falsey. The (deduced) sync method can be any closure to be synced
         *                            on <tt>targets</tt>, though it will normally operate on said target(s). If a string,
         *                            such a (private allowed) function is expected to be defined on each target. Sync
         *                            events triggered for each target in <tt>targets</tt> will never use a private method
         *                            name, e.g. "_save" will be triggered as "save".
         * @param {*|[]}    targets   The target(s) in question; cannot be falsey. Each target in must be a type that is
         *                            a mutable object, e.g. cannot assign properties to primitive or read-only types.
         * @param {...*}    [argumentsJsDoc] Arguments, as var-args, for the actual sync method when invoked, if any. Will
         *                            default to an empty array of arguments.
         *
         * @return {Queue|Promise} The queue handling the sync request(s) <b>or</b> a promise is so specified for
         *                         the enqueued requests (not for the queue); never falsey. If a queue, obtain a promise
         *                         that that will resolve once all sync requests have completed via
         *                         <tt>returnedQueue.promise()</tt>. To cancel the queue, call
         *                         <tt>returnedQueue.cancel()</tt>.
         * @throws {Error} If <tt>options</tt> is a string and no such (private) sync function is defined on the
         *                 target(s), or if a target is not a mutable object.
         *
         * @see    #queue
         * @see    #pending
         * @see    #cancel
         * @see    #cancelable
         */
        // eslint-disable-next-line no-unused-vars
        sync(options, targets, argumentsJsDoc, ...args) { // (method, targets, ..)
            // "Promise mode", i.e. assumed to be making a request for some data outside model scope...
            if (options.promise) {
                // Give me data and not model...
                if (!options.resultHandler) {
                    options.resultHandler = (data) => data;
                }
                // Suppress timeouts...
                if (!options.errorHandler) {
                    options.errorHandler = () => false;
                }
            }

            const syncArgs = [argumentsJsDoc].concat(args);

            // Do we need a bulk queue for explicit ordering between different target entities? Only if an array
            // of targets is supplied with a length larger than one...
            if (_.isArray(targets)) {
                if (targets.length > 1) {
                    return this._syncMany(options, targets, syncArgs); // default sequential!
                }
                targets = targets[0];
                // Fall-through
            }
            // ..or just a single target, in which case a target queue will suffice!?...
            return this._syncOne(options, targets, syncArgs);
        },

        /**
         * @private
         * Performs the specified sync method on the supplied target, returning a promise that will
         * resolve when the sync request has completed.
         *
         * @param {String|{method:String, fn:Function}} options The sync method, e.g. "save" or options like
         *                                                      <tt>{method: "foo", fn: this.foo.bind(this)}</tt>;
         *                                                      never falsey. The <tt>fn</tt> can be any closure to
         *                                                      be synced on <tt>targets</tt>, though it will normally
         *                                                      operate on said targets. If a string, such a (private
         *                                                      allowed) function is expected to be defined on
         *                                                      <tt>target</tt>.
         * @param {*}      target The target in question; never null, but can be empty.
         * @param {[]}     syncOneArgs   Arguments for the sync method when invoked; never falsey, but can be empty.
         *
         * @return {Queue|Promise} The queue used for the sync <b>or</b> the promise for the request just
         *                         enqueued if <tt>options.promise</tt> is truthy; never null.
         *
         * @see    #_syncMany
         */
        _syncOne(options, target, syncOneArgs) {
            // (Re-)use *target queue* for the specified target and ensure sync request...
            const tq = this.queue(target);

            // Enqueue...
            const promise = tq.enqueue(options, syncOneArgs);

            // Start dequeuing, if not already, and when ready. That is, the queue may be blocked, now or later,
            // until ready to invoke the current sync request...
            tq.sync(); // note: returns promise that resolve on queue empty, not what we need here!

            // Return queue *or* promise if so requested...
            return options.promise ? promise : tq;
        },

        /**
         * @private
         * Performs the specified sync method on all supplied targets in sequential or parallel order, returning a
         * promise that will resolve when all sync requests have completed.
         *
         * @param {String|{method:String, fn:Function, ..}} options The sync method, e.g. "save" or options like
         *                                                          <tt>{method: "foo", fn: this.foo.bind(this), ..}</tt>;
         *                                                          never falsey. The <tt>fn</tt> can be any closure to
         *                                                          be synced on <tt>targets</tt>, though it will normally
         *                                                          operate on said targets. If a string, such a (private
         *                                                          allowed) function is expected to be defined on each
         *                                                          target.
         * @param {[]} targets The targets in question; never null, but can be empty.
         * @param {[]} args    Arguments for the sync method when invoked; never falsey, but can be empty.
         *
         * @return {Queue|Promise} The queue used for the sync <b>or</b> the promise for the request just
         *                         enqueued for all targets if <tt>options.promise</tt> is truthy; never null.
         *
         * @see    #_syncOne
         */
        _syncMany(options, targets, args) {
            // Create sequential/parallel bulk queue...
            const bk = this.queue(options.parallel, options.result);

            // Enqueue the sync method for all targets...
            const promise = bk.enqueue(options, targets, args);

            // Start the sync (if not already by nested target queues)...
            bk.sync();

            // Return bulk queue...
            return options.promise ? promise : bk;
        },

        /**
         * Convenient for performing a silent http(s) GET request for the specified relative path, resolving to the
         * returned result as-is. <p>
         *
         * The default sync options used are:
         * <pre>
         *    method:    ..,   // last part from "location", e.g. "foo" from "bar/foo?optional-query-parameters".
         *    immutable: true,
         *    promise:   true  // per request, not per queue
         *    silent:    true, // no notifications on errors, use "fail(..) handler where required
         *    http:      true,
         *    fn:        { type: "GET", url: "path" qualified }
         * </pre>
         *
         * @param {String} location  The absolute URL or the relative path of the url, will be qualified with the base
         *                           url from the REST api; cannot be falsey.
         * @param {*}      target    The target to specify the queue to use; cannot be falsey.
         * @param {*}      [options] Additional options to use for the request, if any; can be falsey.
         *
         * @return {Promise} A promise that will resolve with the raw result from the GET request; never falsey.
         */
        get(location, target, options) {
            // Can be null if called before REST api is initialised...
            const url = UrlUtil.isAbsoluteUrl(location) ? location : RestApi.qualify(location);
            if (!url) {
                return Promise.rejected();
            }

            const parts  = url.split("/");
            let method = parts[parts.length - 1];
            const query  = method.indexOf("?");

            // Drop query parameters from the method name...
            if (query !== -1) {
                method = method.substring(0, query);
            }

            return this.sync(_.extend({
                method,
                promise:   true,
                immutable: true,
                silent:    true, // will also suppress error notifications!
                fn: {
                    url,
                    type: "GET"
                }

            }, options || {}), target);
        },

        getJSON(location, target, options) {
            const url = UrlUtil.isAbsoluteUrl(location) ? location : RestApi.qualify(location);

            return this.get(location, target, _.extend({
                fn: {
                    url,
                    type: "GET",
                    dataType: "json"
                }
            }, options || {}));
        },

        /**
         * Convenient method for creating a new identified target to sync on (to identify a unique queue). <p>
         *
         * The returned target is a plain old JS object like:
         * <pre>
         *     {
         *         id:        String,   // the specified id
         *         toString:  Function, // simple returns the specified id
         *         syncState: {}        // sync state for the returned target
         *     }
         * </pre>
         *
         * If <tt>options</tt> are specified, they will be merged into <tt>syncState</tt> as described by
         * <tt>Syncer.State.init(..)</tt>.
         *
         * @param  {*} id        The id to uniquely identify the returned target; cannot be null or undefined.
         * @param  {*} [options] Additional sync options, if any.
         *
         * @return {*} A new target as specified; never falsey.
         */
        target(id, options) {
            const target = {
                id,
                toString: () => String(id)
            };

            Syncer.State.init(target, _.extend({unique: ["id"]}, options));

            return target;
        },

        /**
         * Returns the string representation of this manager.
         *
         * @return {String} The string representation; never falsey.
         */
        toString() {
            return `SyncManager[dequeuing: ${this._dequeuing}; tq: ${this._targetQueuesSize}; bq: ${this._bulkQueues.length}]`;
        }

    });

    return new SyncManager();
});



// WEBPACK FOOTER //
// ./src/shared/services/sync/SyncManager.js